import type { Editor } from '@milkdown/core'
import { slashFactory } from '@milkdown/kit/plugin/slash'
import { SlashProvider } from '@milkdown/kit/plugin/slash'
import { PluginKey, TextSelection } from '@milkdown/prose/state'
import type { EditorState } from '@milkdown/prose/state'
import type { EditorView } from '@milkdown/prose/view'

import {
  FACT_SUGGESTION_COMPOSITION_SETTLE_MS,
  FACT_SUGGESTION_PROVIDER_DEBOUNCE_MS,
  FACT_SUGGESTION_PROVIDER_OFFSET_PX,
  FACT_SUGGESTION_RESULT_LIMIT,
} from '@/config/constants'
import type { CatalogueRow } from '@/features/facts/types'
import { MDAST_JINJA_TOKEN, replaceWithJinjaToken } from './jinjaToken'
import { isFactTokenBody } from './tokenRegex'

export const factSuggestions = slashFactory('FACT_SUGGESTIONS')
const factSuggestionStateKey = new PluginKey<number | null>(
  'factSuggestionState'
)

type FactQuery = {
  from: number
  to: number
  query: string
}

type FactSuggestionSession = FactQuery & {
  organizationId: string
}

export type FactSuggestionStatus = 'loading' | 'ready' | 'error'

export type FactSuggestionSnapshot = {
  organizationId: string | null
  status: FactSuggestionStatus
  rows: readonly CatalogueRow[]
}

export type FactSuggestionSource = {
  getSnapshot: () => FactSuggestionSnapshot
  subscribe: (listener: () => void) => () => void
  retry: () => void
  update: (
    snapshot: FactSuggestionSnapshot,
    retry: () => void | Promise<unknown>
  ) => void
}

const EMPTY_SNAPSHOT: FactSuggestionSnapshot = {
  organizationId: null,
  status: 'ready',
  rows: [],
}

export function createFactSuggestionSource(): FactSuggestionSource {
  let snapshot = EMPTY_SNAPSHOT
  let retryRequest: (() => void | Promise<unknown>) | undefined
  const listeners = new Set<() => void>()
  const notify = () => listeners.forEach((listener) => listener())

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    retry: () => {
      if (snapshot.status !== 'error') return
      snapshot = { ...snapshot, status: 'loading' }
      notify()
      void retryRequest?.()
    },
    update: (next, retry) => {
      retryRequest = retry
      if (
        next.organizationId === snapshot.organizationId &&
        next.status === snapshot.status &&
        next.rows === snapshot.rows
      )
        return
      snapshot = next
      notify()
    },
  }
}

/** True when the view is in a state where a Fact suggestion may be offered. */
export function canSuggest(view: EditorView, composing = false): boolean {
  return view.editable && view.hasFocus() && !composing && !view.composing
}

export function getLiveFactQuery(state: EditorState): FactQuery | null {
  const { selection } = state
  if (!selection.empty || !(selection instanceof TextSelection)) return null

  const { $from } = selection
  if (
    !$from.parent.isTextblock ||
    $from.parent.type.spec.code ||
    $from.parent.type.name === MDAST_JINJA_TOKEN
  )
    return null

  const before = $from.nodeBefore
  if (
    !before?.isText ||
    before.marks.some((mark) => mark.type.spec.code) ||
    state.storedMarks?.some((mark) => mark.type.spec.code)
  )
    return null

  const text = before.text ?? ''
  const open = text.lastIndexOf('{')
  if (open < 0) return null

  // An earlier unclosed `{` only disqualifies this one while it is still a live
  // candidate itself: a Fact body holds no whitespace, so `{{` and `{pat{ient`
  // stay closed but `{vist and also {pat` opens on the last brace.
  const earlier = open > 0 ? text.lastIndexOf('{', open - 1) : -1
  if (
    earlier >= 0 &&
    earlier > text.lastIndexOf('}', open - 1) &&
    !/\s/.test(text.slice(earlier + 1, open))
  )
    return null

  const candidate = text.slice(open)
  const query = candidate.slice(1)
  // Whitespace and braces close the menu: they can never become a Fact body, so
  // a padded query like `{ pat` would only ever render "No matching Facts".
  if (query && !isFactTokenBody(query)) return null

  return {
    from: selection.from - candidate.length,
    to: selection.from,
    query,
  }
}

// Hoisted: `localeCompare` builds an Intl collator per call, and this sort runs
// on every keystroke over the whole catalogue.
const FACT_NAME_COLLATOR = new Intl.Collator(undefined, {
  sensitivity: 'base',
})

export function rankFactSuggestions(
  rows: readonly CatalogueRow[],
  query: string,
  limit = FACT_SUGGESTION_RESULT_LIMIT
): CatalogueRow[] {
  const needle = query.toLowerCase()
  const unique = new Map<string, { row: CatalogueRow; score: number }>()

  for (const row of rows) {
    // A binding that isn't a valid token body would insert a pill the
    // normalizer immediately dissolves back to prose. Don't offer it.
    if (unique.has(row.name) || !isFactTokenBody(row.name)) continue
    const score = factMatchScore(row, needle)
    if (score >= 0) unique.set(row.name, { row, score })
  }

  return [...unique.values()]
    .toSorted(
      (a, b) =>
        a.score - b.score ||
        FACT_NAME_COLLATOR.compare(a.row.name, b.row.name) ||
        a.row.name.localeCompare(b.row.name)
    )
    .slice(0, limit)
    .map(({ row }) => row)
}

function factMatchScore(row: CatalogueRow, needle: string): number {
  const name = row.name.toLowerCase()
  if (!needle || name === needle) return 0
  if (name.startsWith(needle)) return 1
  if (name.includes(needle)) return 2
  return row.description.toLowerCase().includes(needle) ? 3 : -1
}

export function acceptFactSuggestion(
  view: EditorView,
  session: FactSuggestionSession,
  snapshot: FactSuggestionSnapshot,
  binding: string
): boolean {
  const live = canSuggest(view) ? getLiveFactQuery(view.state) : null
  if (
    !live ||
    !isFactTokenBody(binding) ||
    live.from !== session.from ||
    live.to !== session.to ||
    live.query !== session.query ||
    snapshot.organizationId !== session.organizationId ||
    !snapshot.rows.some(
      (row) =>
        row.name === binding &&
        factMatchScore(row, live.query.toLowerCase()) >= 0
    )
  )
    return false

  const type = view.state.schema.nodes[MDAST_JINJA_TOKEN]
  if (!type) return false

  try {
    view.dispatch(
      replaceWithJinjaToken(
        view.state,
        type,
        live.from,
        live.to,
        `{${binding}}`
      )
    )
    view.focus()
    return true
  } catch {
    return false
  }
}

let menuId = 0

class FactSuggestionView {
  private activeIndex = 0
  private composing = false
  private compositionTimer: ReturnType<typeof setTimeout> | null = null
  private open = false
  private results: CatalogueRow[] = []
  private session: FactSuggestionSession | null = null
  private readonly content: HTMLDivElement
  private readonly list: HTMLDivElement
  private readonly liveRegion: HTMLDivElement
  private readonly hint: HTMLSpanElement
  private readonly provider: SlashProvider
  private readonly unsubscribe: () => void

  constructor(
    private view: EditorView,
    private readonly source: FactSuggestionSource
  ) {
    const id = `fact-suggestions-${++menuId}`
    this.content = document.createElement('div')
    this.content.id = id
    this.content.className = 'milkdown-slash-menu fact-suggestions'
    this.content.dataset.show = 'false'
    this.content.addEventListener('pointermove', this.handlePointerMove)
    this.content.addEventListener('pointerdown', this.handlePointerDown)
    this.content.addEventListener('mousedown', this.handleMouseDown)
    this.content.addEventListener('click', this.handleClick)

    // A listbox may only own option/group children, so the role goes on the
    // options wrapper — the header and live region are siblings, not options.
    this.list = document.createElement('div')
    this.list.id = `${id}-list`
    this.list.className = 'fact-suggestions__list'
    this.list.setAttribute('role', 'listbox')
    this.list.setAttribute('aria-label', 'Facts')

    // Built ONCE: an aria-live region only announces mutations to a region the
    // AT has already registered, so one rebuilt per render announces nothing.
    this.liveRegion = document.createElement('div')
    this.liveRegion.className = 'sr-only'
    this.liveRegion.setAttribute('role', 'status')
    this.liveRegion.setAttribute('aria-live', 'polite')

    const header = document.createElement('div')
    header.className = 'fact-suggestions__header'
    const title = document.createElement('span')
    title.textContent = 'Facts'
    this.hint = document.createElement('span')
    this.hint.className = 'fact-suggestions__hint'
    header.append(title, this.hint)
    this.content.append(header, this.list, this.liveRegion)

    // Standing capabilities; the widget-state attributes go on in `onShow`.
    this.view.dom.setAttribute('aria-haspopup', 'listbox')
    this.view.dom.setAttribute('aria-controls', this.list.id)
    this.view.dom.setAttribute('aria-expanded', 'false')

    this.provider = new SlashProvider({
      content: this.content,
      debounce: FACT_SUGGESTION_PROVIDER_DEBOUNCE_MS,
      offset: FACT_SUGGESTION_PROVIDER_OFFSET_PX,
      shouldShow: (editorView) => this.shouldShow(editorView),
    })
    this.provider.onShow = () => {
      this.open = true
      // A role-less contenteditable drops `aria-activedescendant`, so the menu
      // needs `textbox` — but only while it is live: a rich editor permanently
      // marked `textbox` pushes screen readers into forms mode and flattens
      // heading/list navigation.
      this.view.dom.setAttribute('role', 'textbox')
      this.view.dom.setAttribute('aria-multiline', 'true')
      this.view.dom.setAttribute('aria-autocomplete', 'list')
      this.view.dom.setAttribute('aria-expanded', 'true')
      this.render()
    }
    this.provider.onHide = () => {
      this.open = false
      this.view.dom.removeAttribute('role')
      this.view.dom.removeAttribute('aria-multiline')
      this.view.dom.removeAttribute('aria-autocomplete')
      this.view.dom.setAttribute('aria-expanded', 'false')
      this.view.dom.removeAttribute('aria-activedescendant')
      // Clear so reopening in the same state is a mutation AT will announce.
      this.liveRegion.textContent = ''
    }

    this.unsubscribe = source.subscribe(this.handleSourceChange)
    this.update(view)
  }

  update = (view: EditorView, previous?: EditorState): void => {
    this.view = view
    this.provider.update(view, previous)
  }

  setComposing = (composing: boolean): void => {
    this.composing = composing
    if (this.compositionTimer) clearTimeout(this.compositionTimer)
    this.compositionTimer = null
    if (composing) {
      this.provider.hide()
      return
    }
    this.compositionTimer = setTimeout(() => {
      this.compositionTimer = null
      this.provider.update(this.view)
    }, FACT_SUGGESTION_COMPOSITION_SETTLE_MS)
  }

  hide = (): void => {
    this.provider.hide()
  }

  handleKeyDown = (_view: EditorView, event: KeyboardEvent): boolean => {
    if (!this.open || this.composing || event.isComposing) return false

    if (event.key === 'Escape') {
      const query = this.liveQuery()
      if (query)
        this.view.dispatch(
          this.view.state.tr.setMeta(factSuggestionStateKey, query.from)
        )
      event.preventDefault()
      this.provider.hide()
      return true
    }

    const snapshot = this.source.getSnapshot()
    if (event.key === 'Enter' && snapshot.status === 'error') {
      event.preventDefault()
      this.source.retry()
      this.view.focus()
      return true
    }

    if (!this.results.length) return false
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      this.activeIndex =
        (this.activeIndex + direction + this.results.length) %
        this.results.length
      this.renderActiveOption(true)
      return true
    }
    if (event.key !== 'Enter') return false

    event.preventDefault()
    this.acceptActive()
    return true
  }

  destroy = (): void => {
    if (this.compositionTimer) clearTimeout(this.compositionTimer)
    this.unsubscribe()
    this.provider.destroy()
    this.content.removeEventListener('pointermove', this.handlePointerMove)
    this.content.removeEventListener('pointerdown', this.handlePointerDown)
    this.content.removeEventListener('mousedown', this.handleMouseDown)
    this.content.removeEventListener('click', this.handleClick)
    this.content.remove()
    for (const attribute of [
      'aria-activedescendant',
      'aria-autocomplete',
      'aria-controls',
      'aria-expanded',
      'aria-haspopup',
      'aria-multiline',
      'role',
    ])
      this.view.dom.removeAttribute(attribute)
  }

  private liveQuery(view = this.view): FactQuery | null {
    return canSuggest(view, this.composing)
      ? getLiveFactQuery(view.state)
      : null
  }

  private shouldShow(view: EditorView): boolean {
    const query = this.liveQuery(view)
    if (!query) return false
    if (!this.source.getSnapshot().organizationId) return false
    return factSuggestionStateKey.getState(view.state) !== query.from
  }

  /** A closed menu never renders, so a late snapshot (Clerk populating the
   * organization after the brace was typed) has to re-run `shouldShow`. */
  private handleSourceChange = (): void => {
    if (this.open) this.render()
    else this.provider.update(this.view)
  }

  private render = (): void => {
    if (!this.open) return

    const query = this.liveQuery()
    const snapshot = this.source.getSnapshot()
    if (
      !query ||
      !snapshot.organizationId ||
      factSuggestionStateKey.getState(this.view.state) === query.from
    ) {
      this.provider.hide()
      return
    }

    this.session = {
      ...query,
      organizationId: snapshot.organizationId,
    }
    this.results =
      snapshot.status === 'ready'
        ? rankFactSuggestions(snapshot.rows, query.query)
        : []
    this.activeIndex = 0

    let announcement = ''
    const rows: HTMLDivElement[] = []

    if (snapshot.status === 'loading') {
      announcement = 'Loading Facts'
      rows.push(this.stateRow(announcement))
    } else if (snapshot.status === 'error') {
      announcement = 'Facts could not be loaded'
      const retry = this.stateRow('Facts could not be loaded · Retry')
      retry.id = `${this.content.id}-retry`
      retry.dataset.factRetry = ''
      retry.setAttribute('role', 'option')
      retry.setAttribute('aria-selected', 'true')
      rows.push(retry)
    } else if (!this.results.length) {
      announcement = query.query ? 'No matching Facts' : 'No Facts available'
      rows.push(this.stateRow(announcement))
    } else {
      announcement = `${this.results.length} Fact suggestions`
      rows.push(...this.results.map(this.optionRow))
    }

    // Enter retries in the error state and does nothing without results, so
    // advertising "Enter Insert" everywhere would name the wrong action.
    this.hint.textContent =
      snapshot.status === 'error'
        ? 'Enter Retry'
        : this.results.length
          ? '↑↓ Navigate · Enter Insert'
          : ''

    this.list.toggleAttribute('aria-busy', snapshot.status === 'loading')
    this.list.replaceChildren(...rows)
    this.liveRegion.textContent = announcement
    this.renderActiveOption()
  }

  /** Non-option row (loading / empty). Hidden from the listbox so it can't be
   * mistaken for a value; the live region carries its text. */
  private stateRow(text: string): HTMLDivElement {
    const row = document.createElement('div')
    row.className = 'fact-suggestions__state'
    row.setAttribute('role', 'presentation')
    row.textContent = text
    return row
  }

  private optionRow = (row: CatalogueRow, index: number): HTMLDivElement => {
    const option = document.createElement('div')
    option.id = `${this.content.id}-option-${index}`
    option.className = 'fact-suggestions__option'
    option.dataset.factIndex = String(index)
    option.setAttribute('role', 'option')

    const binding = document.createElement('span')
    binding.className = 'fact-suggestions__binding'
    binding.textContent = `{${row.name}}`
    option.append(binding)

    if (row.description) {
      const description = document.createElement('span')
      description.className = 'fact-suggestions__description'
      description.textContent = row.description
      option.append(description)
    }
    return option
  }

  /** `scroll` only from keyboard nav — scrolling under a hovering pointer, or
   * on a freshly rebuilt list, is both wasted layout and jarring. */
  private renderActiveOption(scroll = false): void {
    const options =
      this.content.querySelectorAll<HTMLElement>('[data-fact-index]')
    options.forEach((option, index) => {
      option.setAttribute('aria-selected', String(index === this.activeIndex))
    })

    // The error state renders a retry row instead of options.
    const active =
      options[this.activeIndex] ??
      this.content.querySelector<HTMLElement>('[data-fact-retry]')
    if (!active) {
      this.view.dom.removeAttribute('aria-activedescendant')
      return
    }

    this.view.dom.setAttribute('aria-activedescendant', active.id)
    if (scroll) active.scrollIntoView?.({ block: 'nearest' })
  }

  private acceptActive(): void {
    const binding = this.results[this.activeIndex]?.name
    if (binding && this.session)
      acceptFactSuggestion(
        this.view,
        this.session,
        this.source.getSnapshot(),
        binding
      )
    this.provider.hide()
  }

  private handlePointerMove = (event: Event): void => {
    const target =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-fact-index]')
        : null
    if (!target) return
    const nextIndex = Number(target.dataset.factIndex)
    if (nextIndex === this.activeIndex) return
    this.activeIndex = nextIndex
    this.renderActiveOption()
  }

  private handlePointerDown = (event: PointerEvent): void => {
    if (!event.isPrimary || event.button !== 0) return
    const option =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-fact-index]')
        : null
    if (!option) return
    this.activeIndex = Number(option.dataset.factIndex)
    this.renderActiveOption()
  }

  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0) return
    const target = event.target instanceof Element ? event.target : null
    if (
      target?.closest('[data-fact-index]') ||
      target?.closest('[data-fact-retry]')
    )
      event.preventDefault()
  }

  private handleClick = (event: MouseEvent): void => {
    if (event.button !== 0) return
    const target = event.target instanceof Element ? event.target : null
    if (!target) return

    if (target.closest('[data-fact-retry]')) {
      event.preventDefault()
      this.view.focus()
      this.source.retry()
      return
    }

    const option = target.closest<HTMLElement>('[data-fact-index]')
    if (!option) return
    event.preventDefault()
    this.activeIndex = Number(option.dataset.factIndex)
    this.view.focus()
    this.acceptActive()
  }
}

export function registerFactSuggestions(
  editor: Editor,
  source: FactSuggestionSource
): void {
  editor
    .config((ctx) => {
      let pluginView: FactSuggestionView | undefined
      ctx.set(factSuggestions.key, {
        key: factSuggestionStateKey,
        state: {
          init: () => null,
          apply: (tr, dismissedFrom, _oldState, newState) => {
            const nextDismissedFrom = tr.getMeta(factSuggestionStateKey)
            if (typeof nextDismissedFrom === 'number') return nextDismissedFrom
            if (dismissedFrom === null) return null

            const mapped = tr.mapping.mapResult(dismissedFrom, 1)
            if (
              mapped.deleted ||
              mapped.pos >= newState.doc.content.size ||
              newState.doc.textBetween(mapped.pos, mapped.pos + 1) !== '{'
            )
              return null

            const query = getLiveFactQuery(newState)
            return query && query.from !== mapped.pos ? null : mapped.pos
          },
        },
        view: (view) => {
          pluginView = new FactSuggestionView(view, source)
          return pluginView
        },
        props: {
          handleKeyDown: (view, event) =>
            pluginView?.handleKeyDown(view, event) ?? false,
          handleDOMEvents: {
            blur: () => {
              pluginView?.hide()
              return false
            },
            focus: (view) => {
              pluginView?.update(view)
              return false
            },
            compositionstart: () => {
              pluginView?.setComposing(true)
              return false
            },
            compositionend: () => {
              pluginView?.setComposing(false)
              return false
            },
          },
        },
      })
    })
    .use(factSuggestions)
}
